"use client";

import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { calculatePremium } from "@/api/simpego";
import { ProductVariant } from "@/hooks/useFunnelState";
import { useErrorDialog } from "@/context/ErrorDialogContext";
import { Address, ContactDetails, Customer } from "@/types/simpego-api"; // Import necessary types
import productData from "@/data/productData.json";
import * as Sentry from "@sentry/react";

const typedProductData = productData as any;

interface UsePremiumCalculationProps {
  petType: "cat" | "dog" | null;
  petName: string | null;
  breed: string | null;
  birthDate: Date | undefined;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  gender: "male" | "female" | "unknown" | null;
  isCastrated: boolean;
  isHouseCat: boolean;
  selectedDogSize: "small" | "large" | null;
  selectedProduct: ProductVariant | null;
  preexistingCoverage: boolean;
  worldwideCoverage: boolean;
  yearlyDeductible: number;
  coPayCoveragePercentage: number;
  paymentInterval: 1 | 2 | 4 | 12;
  paymentMethod: "credit_card" | "ESR";
  startOfInsurance: Date;
  contractId: string | null;
  contractToken: string | null;
  agentId: string | null; // New prop for agentId
  currentLocale: string;
  whitelabelPartner: string | null;
}

const formatDateLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const usePremiumCalculation = ({
  petType,
  petName,
  breed,
  birthDate,
  selectedBreedType,
  gender,
  isCastrated,
  isHouseCat,
  selectedDogSize,
  selectedProduct,
  preexistingCoverage,
  worldwideCoverage,
  yearlyDeductible,
  coPayCoveragePercentage,
  paymentInterval,
  paymentMethod,
  startOfInsurance,
  contractId,
  contractToken,
  agentId, // Destructure new prop
  whitelabelPartner,
  currentLocale,
}: UsePremiumCalculationProps) => {
  const [underwritingFailed, setUnderwritingFailed] = useState<boolean>(false);
  const [underwritingReason, setUnderwritingReason] = useState<string | null>(null);
  const { showErrorDialog } = useErrorDialog();

  const DEFAULT_AGENT_ID = typeof window !== 'undefined' && (window as any).__env__?.defaultAgentId ? (window as any).__env__.defaultAgentId : "VA0000000003";

  const buildPayload = useCallback((
    petTypeArg: "cat" | "dog",
    petNameArg: string,
    breedArg: string | null,
    birthDateArg: Date,
    selectedBreedTypeArg: "purebred" | "crossbreed" | "mixed",
    genderArg: "male" | "female" | "unknown",
    isCastratedArg: boolean,
    isHouseCatArg: boolean,
    selectedDogSizeArg: "small" | "large" | null,
    product: ProductVariant,
    preexistingCoverageArg: boolean,
    worldwideCoverageArg: boolean,
    yearlyDeductibleArg: number,
    coPayCoveragePercentageArg: number,
    paymentIntervalArg: 1 | 2 | 4 | 12,
  paymentMethodArg: "credit_card" | "ESR",
  startOfInsuranceArg: Date,
  customerNameArg: string,
  customerEmailArg: string,
  locale: string,
  agentIdArg: string | null
) => {
    const formattedPetBirthdate = formatDateLocal(birthDateArg);
    const safeStartOfInsurance = startOfInsuranceArg ?? new Date();
    const formattedStartOfInsurance = formatDateLocal(safeStartOfInsurance);
    const customerBirthdate = "1980-01-01";

    const getRecommendedCoPay = (petType: "cat" | "dog", dob: Date): number => {
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      if (age < 9) {
        return typedProductData.customizationOptions?.[petType]?.upTo9Yrs?.claimDeductiblePercentageUpTo9YrsRecommended ?? 80;
      }
      return typedProductData.customizationOptions?.[petType]?.Over9Yrs?.claimDeductiblePercentageOverTo9YrsRecommended ?? 80;
    };
    const getRecommendedDeductible = (petType: "cat" | "dog", dob: Date): number => {
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      if (age < 9) {
        return typedProductData.customizationOptions?.[petType]?.upTo9Yrs?.annualDeductiblesRecommended ?? 300;
      }
      return typedProductData.customizationOptions?.[petType]?.Over9Yrs?.annualDeductiblesRecommended ?? 300;
    };

    const safeCoPayCoverage = Number.isFinite(coPayCoveragePercentageArg)
      ? coPayCoveragePercentageArg
      : getRecommendedCoPay(petTypeArg, birthDateArg);
    const safeAnnualDeductible = Number.isFinite(yearlyDeductibleArg)
      ? yearlyDeductibleArg
      : getRecommendedDeductible(petTypeArg, birthDateArg);

    const claimDeductiblePercentageUpTo9Yrs = 100 - safeCoPayCoverage;
    const claimDeductiblePercentageOver9Yrs = product.details.disease.claim_deductible_percentage_over_9_yrs;

    let finalBreed: string | null = null;
    let isCrossbreedFlag = false;

    if (selectedBreedTypeArg === "purebred" || selectedBreedTypeArg === "crossbreed") {
      finalBreed = breedArg;
      isCrossbreedFlag = selectedBreedTypeArg === "crossbreed";
    } else if (selectedBreedTypeArg === "mixed") {
      isCrossbreedFlag = false;
      if (petTypeArg === "cat") {
        finalBreed = "mischlingskatze";
      } else if (petTypeArg === "dog") {
        finalBreed = selectedDogSizeArg === "small" ? "mischling_klein" : "mischling_gross";
      }
    }

    const dummyCustomerAddress: Address = {
      street: "Musterstrasse",
      housenumber: "1",
      zip: "8000",
      city: "Zürich",
      countryCode: "CH",
    };

    const dummyCustomerContactDetails: ContactDetails[] = [
      { value: "dummy@example.com", category: "email" }
    ];

    const dummyCustomer: Customer = {
      objectType: "NaturalPerson",
      firstname: "Max",
      name: "Muster",
      birthdate: customerBirthdate,
      email: "dummy@example.com",
      gender: "male",
      language: locale.toUpperCase() as "DE" | "FR" | "IT" | "EN",
      address: dummyCustomerAddress,
      contactDetails: dummyCustomerContactDetails,
    };

    return {
      contractID: contractId || undefined,
      contractToken: contractToken || undefined,
      startOfInsurance: formattedStartOfInsurance,
      documentDispatch: {
        dispatch: "electronic",
      },
      paymentMethod: paymentMethodArg ?? "credit_card",
      yearlyPaymentFrequency: {
        frequency: paymentIntervalArg ?? 12,
      },
      agentID: agentIdArg || DEFAULT_AGENT_ID,
      ...(whitelabelPartner ? { whitelabelPartner } : {}),
      product: {
        productName: "pet",
        pets: [
          {
            species: petTypeArg === "dog" ? "Dog" : "Cat",
            name: petNameArg,
            birthdate: formattedPetBirthdate,
            breed: finalBreed,
            crossbreed: isCrossbreedFlag,
            gender: genderArg,
            castration: isCastratedArg,
            housecat: petTypeArg === "cat" ? isHouseCatArg : undefined,
            productBundle: product.id.toUpperCase() as "S" | "M" | "L",
            coverages: {
              disease: {
                requested: true,
                annualDeductible: safeAnnualDeductible,
                claimDeductiblePercentageUpTo9Yrs,
                claimDeductiblePercentageOver9Yrs,
                sumInsured: product.details.disease.sum_insured,
                dentalTreatmentAccident: product.details.disease.dental_treatment_accident,
                dentalTreatmentDisease: product.details.disease.dental_treatment_disease,
                emergencyCare: product.details.disease.emergency_care,
                preventiveTreatment: product.details.disease.preventive_treatment,
                alternativeTreatment: product.details.disease.alternative_treatment,
                advancedTreatment: product.details.disease.advanced_treatment,
                medicalFood: product.details.disease.medical_food,
                medicalFoodCap: product.details.disease.medical_food_cap,
                behaviourTreatment: product.details.disease.behaviour_treatment,
                emergencyTreatment: product.details.disease.emergency_treatment,
              },
              preexistingConditions: {
                requested: preexistingCoverageArg,
                claimDeductible: product.details.preexisting_condition.claim_deductible,
                claimDeductiblePercentageUpTo9Yrs: product.details.preexisting_condition.claim_deductible_percentage_up_to_9_yrs,
                claimDeductiblePercentageOver9Yrs: product.details.preexisting_condition.claim_deductible_percentage_over_9_yrs,
                sumInsuredCurrentYear: product.details.preexisting_condition.sum_insured_current_year,
                sumInsuredNextYear: product.details.preexisting_condition.sum_insured_next_year,
              },
              worldwideCoverage: {
                requested: !!worldwideCoverageArg,
                sumInsured: product.details.worldwide_coverage.sum_insured,
              },
            },
          },
        ],
      },
      customer: dummyCustomer,
    };
  }, [contractId, contractToken, DEFAULT_AGENT_ID, whitelabelPartner]);

  const fetchPremium = useCallback(async (payload: any): Promise<{ price: number | null; failed: boolean; reason: string | null }> => {
    try {
      const response = await calculatePremium(payload);
      if (response.underwriting === false) {
        const reason = response.underwritingReason || response.feedbackReason || "Unknown underwriting reason.";
        return { price: null, failed: true, reason: reason };
      }
      return { price: response.grossPremiumProRata || null, failed: false, reason: null };
    } catch (error) {
      Sentry.captureException(error);
      showErrorDialog("Failed to fetch premium. Please try again.");
      return { price: null, failed: true, reason: "API call failed." };
    }
  }, [showErrorDialog]);

  const fetchInitialBundlePrices = useCallback(async (
    petTypeArg: "cat" | "dog",
    petNameArg: string,
    breedArg: string | null,
    birthDateArg: Date | undefined,
    selectedBreedTypeArg: "purebred" | "crossbreed" | "mixed",
    genderArg: "male" | "female" | "unknown",
    isCastratedArg: boolean,
    isHouseCatArg: boolean,
    selectedDogSizeArg: "small" | "large" | null,
    preexistingCoverageArg: boolean,
    customerNameArg: string,
    customerEmailArg: string,
    locale: string,
    agentIdArg: string | null,
    worldwideCoverageArg: boolean,
    yearlyDeductibleArg: number,
    coPayCoveragePercentageArg: number,
    paymentIntervalArg: 1 | 2 | 4 | 12,
    paymentMethodArg: "credit_card" | "ESR",
    startOfInsuranceArg: Date
  ) => {
    if (!petTypeArg || !petNameArg || !breedArg || !birthDateArg || !selectedBreedTypeArg || !genderArg) {
      console.log("Missing data for initial bundle price calculation.");
      return null;
    }

    const productVariants = [
      { id: "s", details: typedProductData.v1.s },
      { id: "m", details: typedProductData.v1.m },
      { id: "l", details: typedProductData.v1.l },
    ];

    const prices: { S: number; M: number; L: number } = { S: 0, M: 0, L: 0 };
    let hasUnderwritingFailure = false;
    let failureReason: string | null = null;

    for (const variant of productVariants) {
      const payload = buildPayload(
        petTypeArg,
        petNameArg,
        breedArg,
        birthDateArg,
        selectedBreedTypeArg,
        genderArg,
        isCastratedArg,
        isHouseCatArg,
        selectedDogSizeArg,
        variant as ProductVariant,
        preexistingCoverageArg,
        worldwideCoverageArg,
        yearlyDeductibleArg,
        coPayCoveragePercentageArg,
        paymentIntervalArg,
        paymentMethodArg,
        startOfInsuranceArg,
        customerNameArg,
        customerEmailArg,
        locale,
        agentIdArg
      );
      const { price, failed, reason } = await fetchPremium(payload);
      if (failed) {
        hasUnderwritingFailure = true;
        failureReason = reason;
        break;
      }
      prices[variant.id.toUpperCase() as "S" | "M" | "L"] = price!;
    }

    if (hasUnderwritingFailure) {
      showErrorDialog(failureReason || "An unknown error occurred during underwriting.");
      setUnderwritingFailed(false);
      setUnderwritingReason(null);
      return null;
    }

    // Clear any prior underwriting error state when prices are fetched successfully.
    setUnderwritingFailed(false);
    setUnderwritingReason(null);

    return prices;
  }, [buildPayload, fetchPremium, showErrorDialog]);

  // Use react-query for automatic refetching and loading state management
  const { isLoading, data: currentApiPrice, refetch } = useQuery({
    queryKey: [
      "premium",
      petType,
      petName,
      breed,
      birthDate?.toISOString(),
      selectedBreedType,
      gender,
      isCastrated,
      isHouseCat,
      selectedDogSize,
      selectedProduct?.id,
      preexistingCoverage,
      worldwideCoverage,
      yearlyDeductible,
      coPayCoveragePercentage,
      paymentInterval,
      paymentMethod,
      startOfInsurance ? startOfInsurance.toISOString() : "no-date",
      contractId,
      contractToken,
      agentId,
      whitelabelPartner,
      currentLocale,
    ],
    queryFn: async () => {
      if (
        !petType ||
        !petName ||
        !breed ||
        !birthDate ||
        !selectedBreedType ||
        !gender ||
        !selectedProduct
      ) {
        // If essential data is missing, we cannot calculate premium.
        // Set underwritingFailed to true and return null to indicate no price.
        setUnderwritingFailed(true);
        setUnderwritingReason("Missing essential pet or product details for premium calculation.");
        return null;
      }

      const payload = buildPayload(
        petType,
        petName,
        breed,
        birthDate,
        selectedBreedType,
        gender,
        isCastrated,
        isHouseCat,
        selectedDogSize,
        selectedProduct,
        preexistingCoverage,
        worldwideCoverage,
        yearlyDeductible,
        coPayCoveragePercentage,
        paymentInterval,
        paymentMethod,
        startOfInsurance ?? new Date(),
        "Placeholder Name", // Dummy customer name
        "placeholder@example.com", // Dummy customer email
        currentLocale || "en", // Default to en if locale missing
        agentId // Pass agentId
      );
      const { price, failed, reason } = await fetchPremium(payload);
      if (failed) {
        setUnderwritingFailed(true);
        setUnderwritingReason(reason);
        return null;
      }
      setUnderwritingFailed(false);
      setUnderwritingReason(null);
      return price;
    },
    enabled: !!(petType && petName && breed && birthDate && selectedBreedType && gender && selectedProduct), // Enable automatic fetching when all required data is present
    onError: (error) => {
      console.error("Query error fetching premium:", error);
      Sentry.captureException(error);
      setUnderwritingFailed(true);
      setUnderwritingReason("Error during premium calculation query.");
    },
  });

  return {
    isLoading,
    fetchInitialBundlePrices,
    currentApiPrice,
    underwritingFailed,
    underwritingReason,
    refetchPrice: refetch, // Expose refetch from useQuery
  };
};
